import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Gauge, CloudOff, ShoppingCart } from 'lucide-react';
import { PANEL_DE_LA_BARRA_TOP_CLASS, PANEL_DE_LA_BARRA_ALTO_MAX_CLASS } from '../lib/medidasDelChasis';

/* dashboard-rediseno (seccion "Panel de creditos", G1 a G3 e I3): EL CHIP DE CREDITOS DE LA BARRA Y SU PANEL.

   El chip es un boton; al tocarlo abre un panel flotante debajo de la barra, alineado a la derecha. El panel
   reemplaza al icono de informacion que el Panel tenia junto a sus creditos: su texto pasa TAL CUAL (las llaves
   `dashboard:credits.tooltip.*`). Se cierra al tocar fuera, al tocar el chip otra vez y con Esc, y al cerrarse el
   foco vuelve al chip.

   LAS CUENTAS SON LAS DE HOY. `n` es lo que queda del mes (`subscription_remaining`), `max` el tope del plan
   (`limit`), `p` los creditos de paquete y "en cero" es `remaining === 0` (la misma condicion del aviso de "te
   quedaste sin creditos" del Panel). Los dias que faltan se cuentan como el Panel los contaba. */

// Los topes del plan de AL LADO para el empujon de la linea del plan: los mismos dos numeros de la tabla del Panel
// (`PLAN_LIMIT_LABELS` de Dashboard.js), que el cliente no puede saber de otra forma.
const EMPUJON = { free: 5, pro: 50 };

const DIA_MS = 1000 * 60 * 60 * 24;

/** Las cuentas del chip, sin pintar nada. */
export function cuentasDelChip(usage, ahora = Date.now()) {
  if (!usage) return null;
  const invitado = usage.plan === 'guest';
  const n = Number(usage.subscription_remaining ?? usage.remaining ?? 0);
  const max = Number(usage.limit ?? 0);
  const p = Number(usage.pack_credits ?? 0);
  let dias = null;
  if (!invitado && usage.credit_reset_date) {
    const reinicio = new Date(usage.credit_reset_date).getTime();
    if (!Number.isNaN(reinicio)) dias = Math.max(1, Math.ceil((reinicio - ahora) / DIA_MS));
  }
  return { invitado, n, max, p, dias, cero: usage.remaining === 0 };
}

/** El texto largo del chip (desde 640). */
export function textoLargo(t, c) {
  if (c.invitado) return t('common:chasis.creditos_chip_invitado', { n: c.n, max: c.max });
  if (c.dias != null && c.p > 0) return t('common:chasis.creditos_chip_paquete', { n: c.n, max: c.max, p: c.p, count: c.dias });
  if (c.dias != null) return t('common:chasis.creditos_chip', { n: c.n, max: c.max, count: c.dias });
  if (c.p > 0) return t('common:chasis.creditos_chip_corto_paquete', { n: c.n, max: c.max, p: c.p });
  return t('common:chasis.creditos_chip_invitado', { n: c.n, max: c.max });
}

/** El texto corto del chip (telefono): solo numeros, o con el paquete entre parentesis. */
export function textoCorto(t, c) {
  if (c.p > 0) return t('common:chasis.creditos_chip_corto_paquete', { n: c.n, max: c.max, p: c.p });
  return `${c.n} / ${c.max}`;
}

// El numero de adelante va en negritas cuando el texto empieza con el (asi es en los dos idiomas).
function conNumeroResaltado(texto, n, claseNumero) {
  const cifra = String(n);
  if (typeof texto !== 'string' || !texto.startsWith(cifra)) return texto;
  return <><span className={claseNumero}>{cifra}</span>{texto.slice(cifra.length)}</>;
}

// dashboard-rediseno, vuelta 2 (D4): la barra le pide dos cosas cuando no cabe. `corto`: el texto corto tambien desde
// 640 (el largo solo cuando cabe). `compacto`: el ultimo escalon, sin el icono y con menos relleno.
export default function ChipDeCreditos({ usage, sinRed = false, puedeComprar = false, onComprar, corto: soloCorto = false, compacto = false }) {
  const { t } = useTranslation(['common', 'dashboard']);
  const [abierto, setAbierto] = useState(false);
  const contenedorRef = useRef(null);
  const chipRef = useRef(null);
  const panelRef = useRef(null);
  const idPanel = useId();

  const cerrar = (devolverFoco = true) => {
    setAbierto(false);
    if (devolverFoco && chipRef.current) chipRef.current.focus();
  };

  useEffect(() => {
    if (!abierto) return undefined;
    // El panel vive en otra rama del documento (su capa propia), asi que "fuera" es fuera del chip Y del panel.
    const alTocarFuera = (e) => {
      const enElChip = contenedorRef.current && contenedorRef.current.contains(e.target);
      const enElPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!enElChip && !enElPanel) setAbierto(false);
    };
    const alTeclear = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setAbierto(false);
        if (chipRef.current) chipRef.current.focus();
      }
    };
    document.addEventListener('mousedown', alTocarFuera);
    document.addEventListener('touchstart', alTocarFuera);
    document.addEventListener('keydown', alTeclear);
    // Al abrir, el foco entra al panel para que Tab recorra lo de adentro.
    if (panelRef.current) panelRef.current.focus();
    return () => {
      document.removeEventListener('mousedown', alTocarFuera);
      document.removeEventListener('touchstart', alTocarFuera);
      document.removeEventListener('keydown', alTeclear);
    };
  }, [abierto]);

  const c = cuentasDelChip(usage);

  // Sin dato todavia: el lugar del chip, vacio, del mismo alto (no brinca al llegar).
  if (!c) {
    return <span aria-hidden="true" className="inline-flex h-11 w-[72px] sm:w-[120px] shrink-0" data-testid="chip-creditos-reserva" />;
  }

  const tono = sinRed ? 'gris' : (c.cero ? 'rojo' : 'normal');
  const pildora = tono === 'rojo'
    ? 'border-[#4A1F1F] bg-[#1B1B1B] text-[#C8C8C8]'
    : 'border-[#2C2C2C] bg-[#1B1B1B] text-[#C8C8C8]';
  const colorIcono = tono === 'rojo' ? 'text-[#F87171]' : (tono === 'gris' ? 'text-[#6A6A6A]' : 'text-[#F7A06A]');
  const colorNumero = tono === 'rojo' ? 'font-semibold text-[#F87171]' : (tono === 'gris' ? 'font-semibold text-[#9A9A9A]' : 'font-semibold text-[#F4F4F3]');
  const largo = textoLargo(t, c);
  const corto = textoCorto(t, c);

  const llenado = c.max > 0 ? Math.max(0, Math.min(100, (c.n / c.max) * 100)) : 0;
  const colorBarra = sinRed ? 'bg-[#4A4A4A]' : (c.cero ? 'bg-[#F87171]' : 'bg-[#F27A2E]');
  const pista = c.cero && !sinRed ? 'bg-[#4A1F1F]' : 'bg-[#262626]';
  const valorDelPanel = c.p > 0
    ? t('common:chasis.creditos_panel_paquete', { n: c.n, max: c.max, p: c.p })
    : `${c.n} / ${c.max}`;
  const verde = { 1: <span key="1" className="text-[#4ADE80]" /> };
  const negritas = { 1: <span key="1" className="font-semibold" /> };

  return (
    <div className="relative shrink-0" ref={contenedorRef} data-testid="chip-creditos-contenedor">
      <button
        type="button"
        ref={chipRef}
        onClick={() => (abierto ? cerrar(true) : setAbierto(true))}
        aria-haspopup="dialog"
        aria-expanded={abierto}
        aria-controls={abierto ? idPanel : undefined}
        className="group flex items-center h-11"
        data-testid="chip-creditos"
        data-tono={tono}
      >
        <span className={`flex items-center gap-1.5 h-8 rounded-full border ${compacto ? 'px-2' : 'px-3'} text-[13px] whitespace-nowrap transition-colors group-hover:bg-[#222222] group-active:bg-[#222222] group-active:duration-0 ${pildora}`}>
          {!compacto && <Gauge className={`w-3.5 h-3.5 shrink-0 ${colorIcono}`} aria-hidden="true" />}
          <span className={soloCorto ? '' : 'sm:hidden'} data-testid="chip-creditos-corto">{conNumeroResaltado(corto, c.n, colorNumero)}</span>
          {!soloCorto && (
            <span className="hidden sm:inline" data-testid="chip-creditos-largo">{conNumeroResaltado(largo, c.n, colorNumero)}</span>
          )}
        </span>
      </button>

      {/* Vuelta 3 (D22 y D3): el panel va en su PROPIA capa fija, colgada del cuerpo de la pagina y no de la barra: un
          nivel (51) arriba de la barra de abajo, para que la pildora no lo tape con el telefono acostado, mientras la
          barra se queda en el 50 de siempre. Sigue debajo de la barra, alineado a la derecha; se cierra igual (fuera,
          el chip y Esc, con el foco de vuelta al chip) y al abrir la ventana de compra. */}
      {abierto && typeof document !== 'undefined' && createPortal(
        <div
          id={idPanel}
          ref={panelRef}
          role="dialog"
          aria-label={t('common:chasis.creditos_titulo')}
          tabIndex={-1}
          className={`fixed right-3 md:right-4 ${PANEL_DE_LA_BARRA_TOP_CLASS} ${PANEL_DE_LA_BARRA_ALTO_MAX_CLASS} z-[51] flex flex-col w-[300px] max-w-[calc(100vw-24px)] rounded-2xl border border-[#2C2C2C] bg-[#1B1B1B] p-4 text-[13px] text-[#C8C8C8] shadow-[0_16px_40px_rgba(0,0,0,0.55)] outline-none`}
          data-testid="panel-creditos"
        >
          {/* Vuelta 2 (D3): si el panel no cabe (telefono acostado), se desplaza por dentro el cuerpo y "Buy credits"
              queda siempre a la vista, abajo. */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" data-testid="panel-creditos-cuerpo">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[15px] font-semibold text-[#F4F4F3]">{t('common:chasis.creditos_titulo')}</p>
            <p className="text-[13px] text-[#9A9A9A]" data-testid="panel-creditos-valor">
              {conNumeroResaltado(valorDelPanel, c.n, colorNumero)}
            </p>
          </div>
          <div className={`mt-2.5 h-1.5 w-full overflow-hidden rounded-full ${pista}`} data-testid="panel-creditos-barra" data-tono={tono}>
            <div className={`h-full rounded-full ${colorBarra}`} style={{ width: `${llenado}%` }} />
          </div>
          {c.dias != null && (
            <p className="mt-2 text-[12px] text-[#9A9A9A]" data-testid="panel-creditos-reinicio">
              {t('dashboard:credits.resets_in_days', { count: c.dias })}
            </p>
          )}
          {sinRed && (
            <div className="mt-3 flex items-center gap-2 rounded-xl border border-[#2C2C2C] bg-[#141414] px-3 py-2 text-[12px] text-[#C8C8C8]" data-testid="panel-creditos-sin-red">
              <CloudOff className="w-4 h-4 shrink-0 text-[#9A9A9A]" aria-hidden="true" />
              <span>{t('common:chasis.creditos_sin_red')}</span>
            </div>
          )}
          <div className="mt-3 border-t border-[#2C2C2C] pt-3">
            <p className="mb-1.5 font-semibold text-[#F4F4F3]">{t('dashboard:credits.tooltip.title')}</p>
            <p className="leading-relaxed text-[#C8C8C8]">
              <Trans i18nKey="dashboard:credits.tooltip.body" components={verde} />
            </p>
            {c.p > 0 && (
              <p className="mt-2 leading-relaxed text-[#C8C8C8]" data-testid="panel-creditos-paquete">
                <Trans i18nKey="dashboard:credits.tooltip.pack_line" count={c.p} values={{ count: c.p }} components={negritas} />
              </p>
            )}
          </div>
          <div className="mt-3 border-t border-[#2C2C2C] pt-3 leading-relaxed text-[#F7A06A]" data-testid="panel-creditos-plan">
            {usage.plan === 'guest' ? (
              <Trans i18nKey="dashboard:credits.tooltip.plan_guest"
                values={{ remaining: usage.remaining, limit: usage.limit, free_limit: EMPUJON.free }} components={negritas} />
            ) : usage.plan === 'free' ? (
              <Trans i18nKey="dashboard:credits.tooltip.plan_free"
                values={{ remaining: usage.subscription_remaining, limit: usage.limit, pro_limit: EMPUJON.pro }} components={negritas} />
            ) : (
              <Trans i18nKey="dashboard:credits.tooltip.plan_pro"
                values={{ remaining: usage.subscription_remaining, limit: usage.limit }} components={negritas} />
            )}
          </div>
          </div>
          {puedeComprar && (
            <div className="mt-3 shrink-0" data-testid="panel-creditos-pie">
              <button
                type="button"
                disabled={sinRed}
                onClick={() => { cerrar(false); if (onComprar) onComprar(); }}
                className="flex w-full h-11 items-center justify-center gap-2 rounded-xl bg-[#F27A2E] text-[14px] font-semibold text-[#160A02] transition-opacity hover:opacity-90 active:opacity-90 active:duration-0 disabled:opacity-45 disabled:cursor-not-allowed"
                data-testid="panel-creditos-comprar"
              >
                <ShoppingCart className="w-4 h-4" aria-hidden="true" />
                {t('dashboard:credits.buy_btn')}
              </button>
              {sinRed && (
                <p className="mt-1.5 text-center text-[12px] text-[#9A9A9A]" data-testid="panel-creditos-necesitas-red">
                  {t('common:offline.need_connection')}
                </p>
              )}
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
